import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { Components } from '@/api/generated/api';
import { TenantTemplateUpsertAction } from '@/composables/schoolAdmin/useCurrentTenant';
import { IAssessmentVisualStat } from '@/core/assessment-visualization/AssessmentVisualStats.vue';
import { EAvatar, EDataTableCellType, EIcon, EStatisticsCalculationType } from 'common/enum/misc.enum';
import type { ISupportMeasureReportOptions } from 'common/interface/support-measure-report.interface';
import { IGenericPerson } from 'common/utils/name.utils';
import { VNode, Component as VueComponent, type StyleValue } from 'vue';
import { RouteNamedMap } from 'vue-router/auto-routes';

export type TTranslate = (key: string, params?: Record<string, unknown>) => string;

export interface IDropdownMultiSelectActionGroup {
  label?: string;
  options: ISelectOption<string>[];
}

export interface IDropdownMultiSelectAction {
  action: (data: string[]) => Promise<void>;
  actionLabel: string;
  groups: IDropdownMultiSelectActionGroup[];
}

export interface IDropdownSubmenuActionOption<T extends number | string = number | string> extends ISelectOption<T> {
  disabled?: boolean;
  hidden?: boolean;
  iconAfter?: VueComponent;
  iconBefore?: EIcon;
  separatorAfter?: boolean;
  separatorBefore?: boolean;
  style?: 'error';
}

export interface IDropdownSubmenuActionGroup<T extends number | string = number | string> {
  id?: number | string;
  label?: string;
  collapsible?: boolean;
  initiallyExpanded?: boolean;
  options: IDropdownSubmenuActionOption<T>[];
}

export interface IDropdownSubmenuAction<T extends number | string = number | string> {
  action: (option: IDropdownSubmenuActionOption<T>) => Promise<void> | void;
  groups: IDropdownSubmenuActionGroup<T>[];
}

export interface IDropdownListAction<T = unknown> {
  action?: (data?: T) => void | Promise<void>;
  hidden?: boolean;
  disabled?: boolean;
  iconBefore?: EIcon;
  iconAfter?: VueComponent;
  label: string | ((row: T) => string);
  multiSelectAction?: IDropdownMultiSelectAction;
  submenuAction?: IDropdownSubmenuAction;
  separatorAfter?: boolean;
  separatorBefore?: boolean;
  style?: 'error';
  requiredFeature?: Components.Schemas.ProductFeature;
  lockedBehavior?: 'disable' | 'emit';
}

export interface IDropdownListActionList<T = unknown> {
  key: string;
  label?: string;
  options: IDropdownListAction<T>[];
  separatorAfter?: boolean;
}

export interface IDataTableAction<T = unknown> extends Omit<IDropdownListAction<T>, 'action' | 'disabled' | 'hidden'> {
  action: (data: T) => void | Promise<void>;
  disabled?: (data: T) => boolean;
  hidden?: (data: T) => boolean;
}

export interface IGenericUser extends IGenericPerson {
  avatar?: string | null;
  avatarPictureFile?: File | null;
  avatarPictureId?: number | null;
  firstName: string | null;
  id?: number;
  isSetupCompleted?: boolean;
  lastName: string | null;
  isInvitation?: boolean;
  isPendingInvitation?: boolean;
  deactivatedAt?: string | null;
}

export type TStudentLike = Pick<
  Components.Schemas.StudentDto,
  'avatar' | 'avatarPictureId' | 'firstName' | 'id' | 'lastName'
>;

export enum EObservationTone {
  Default = 'default',
  Note = 'note',
}

export interface ISelectedCompetence {
  competence: Components.Schemas.CompetenceDto;
  domain: Components.Schemas.CompetenceDomainDto;
}

export interface IUserOrInvitation {
  roleType: Components.Schemas.UserRoleType;
  user: IGenericUser;
  userRole?: Components.Schemas.UserRoleWithUserDto;
  invitation?: Components.Schemas.TenantUserInvitationDto;
}

export interface IGenericSchoolClass {
  name: string;
  nickname?: string | null;
  appearance?: Components.Schemas.EntityAppearanceDto | null;
}

export interface ISelectOption<T = number | string, D = never> {
  label: string;
  value: T;
  selectable?: boolean;
  details?: D;
}

export interface ISelectOptionList<T = number | string> {
  key: string;
  label?: string;
  options: ISelectOption<T>[];
}

export enum EAssessmentVisualizationMode {
  Journal = 'journal',
  Distribution = 'distribution',
  Rating = 'rating',
}

export enum EPerformanceAssessmentStatsVariant {
  LIST = 'list',
  DROPDOWN = 'dropdown',
}

export enum EPerformanceVisualDisabledReason {
  AccessRights = 'access-rights',
  ExistingObservation = 'existing-observation',
  Archived = 'archived',
}

export enum ETooltipBadgeTheme {
  OUTLINE = 'outline',
  FILLED = 'filled',
}

export enum ETooltipBadgeTone {
  DANGER = 'danger',
  SECONDARY = 'secondary',
  TINT = 'tint',
}

export type TAssessmentVisualizationContext = 'grade-calculation-dialog' | 'statistics-table' | 'student-overview';

export interface IImageSelectOption<T = number | string, D = never> extends ISelectOption<T, D> {
  imageSrc: string;
  hintText?: string;
}
export enum ESimpleDataTableSortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum ESimpleDataTableSortMode {
  CLIENT = 'client',
  SERVER = 'server',
}

export interface ISimpleDataTableSort<TKey extends string = string> {
  key: TKey;
  direction: ESimpleDataTableSortDirection;
}

export interface ISimpleDataTableColumn<T> {
  actions?: IDataTableAction<T>[];
  cellClass?: string | ((value: T) => string);
  cellType?: EDataTableCellType;
  format?: (value: T) => string;
  getAssessmentStatsCellConfig?: (value: T) => IAssessmentVisualStat[] | IAssessmentVisualizationCellConfig;
  getSchoolClassCellConfig?: (value: T) => Components.Schemas.SchoolClassDto;
  getSchoolYearCellConfig?: (value: T) => Components.Schemas.SchoolYearDto;
  getSortField?: (value: T) => string;
  getStudentCellConfig?: (value: T) => TStudentLike;
  getSubjectAccessRightCellConfig?: (value: T) => Components.Schemas.BaseSchoolSubjectDto[] | null;
  getUserCellConfig?: (value: T) => IGenericUser;
  getUserOrInvitationCellConfig?: (value: T) => IUserOrInvitation;
  getUserRoleStatusCellConfig?: (value: T) => Components.Schemas.UserRoleStatus;
  getSubscriptionStateCellConfig?: (value: T) => Components.Schemas.SubscriptionState | null;
  headerClass?: string;
  headerStyle?: StyleValue;
  hideSortIndicatorSpacer?: boolean;
  key: string;
  label?: string | ((row: T) => string);
  renderComponent?: (value: T) => VNode;
  sortable: boolean;
  initialSort?: ESimpleDataTableSortDirection;
  studentCellAction?: (value: T) => void | Promise<void>;
  width?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | `${number}px`
    | `${number}fr`
    | `fit-content(${number}%)`
    | `minmax(min-content,${number}fr)`
    | `minmax(${number}px,${number}fr)`;
}

export interface ISimpleDataTableFilterParams {
  classId?: number | null;
  text: string;
}

export interface ISimpleDataTableBulkAction<T> {
  label: string | ((row: T) => string);
  action: (selectedItems: T[]) => void | Promise<void>;
}

export interface ISimpleDataTableSelectionPrimaryAction<T> {
  label: string;
  action: (selectedItems: T[]) => void | Promise<void>;
  iconBefore?: EIcon;
  disabled?: (selectedItems: T[]) => boolean;
}

export interface ISchoolSubjectGridSelectionPrimaryAction {
  label: string;
  action: (selectedItems: Components.Schemas.SchoolSubjectDto[]) => void | Promise<void>;
  iconBefore?: EIcon;
  disabled?: (selectedItems: Components.Schemas.SchoolSubjectDto[]) => boolean;
}

export interface ISchoolSubjectGridSelectionConfig {
  enabled?: boolean;
  countLabel?: (count: number) => string;
  primaryAction?: ISchoolSubjectGridSelectionPrimaryAction;
  secondaryActions?: IDropdownListAction<Components.Schemas.SchoolSubjectDto[]>[];
  leadingIcon?: EIcon;
}

export interface IOnboardingStudent {
  avatarUploadFailed?: boolean;
  id: number | null;
  clientKey?: string;
  firstName: string;
  lastName: string;
  avatar: string | EAvatar;
  avatarPictureFile?: File | null;
  avatarPictureId?: number | null;
  clearAvatarPicture?: boolean;
  existing: boolean;
}

export interface IVisualLevel {
  id: number | null;
  level: number;
  isFrontendPicture: boolean | null;
  picturePath: string | null;
  pictureUrl: string | null;
}

export interface IAssessmentVisualizationHistoryItem {
  date: string;
  entryId: number;
  level: IVisualLevel;
  performanceAssessmentEntryId?: number | null;
}

export interface ISchoolGradeSummaryData {
  history: Array<{ value: number; date: string; entryId: number }>;
  buckets: Array<{ gradeValue: number; count: number }>;
}

export interface IAssessmentRatingSummary {
  valuationPercentage: number;
  ratedAmountTotal: number;
  valueLabel?: string;
}

export interface IAssessmentVisualizationData {
  gradeConfiguration?: ISchoolGradeDisplayConfiguration | null;
  /** An authoritative result for rating mode. Undefined derives it from entries; null means no included data. */
  ratingSummary?: IAssessmentRatingSummary | null;
  schoolGrades?: ISchoolGradeSummaryData;
  history: IAssessmentVisualizationHistoryItem[];
  ratedAmountTotal: number;
  stats: IAssessmentVisualStat[];
  valuationPercentage: number;
}

export interface IAssessmentVisualizationCellConfig extends IAssessmentVisualizationData {
  mode: EAssessmentVisualizationMode;
}

export interface IUserWithPermissions
  extends Omit<Components.Schemas.SchoolClassAccessRightDto, 'id' | 'createdAt' | 'updatedAt' | 'schoolClass'> {
  schoolClass?: null | Components.Schemas.SchoolClassDto;
  schoolSubjects: Components.Schemas.SchoolSubjectDto[];
  isInvitation?: boolean;
  isPendingInvitation?: boolean;
}

export interface IOnboardingPerformanceAssessment {
  id: number | null;
  name: string;
  weighting: number;
  schoolLevel: number;
  isTemplate: boolean;
  isOfficial: boolean;
  schoolClassId: number | null;
  performanceVisual: Components.Schemas.PerformanceVisualDto | null;
  performanceVisualId: number | null;
  documentationMode: Components.Schemas.PerformanceAssessmentDocumentationMode;
  existing: boolean;
  deleted: boolean;
}

export interface IOptionalDateSchoolTerm {
  name: string;
  startDate: string | null;
  endDate: string | null;
  schoolTermSemester: Components.Schemas.SchoolTermSemester;
}

export interface IGenericSchoolSubject {
  name: string;
  id: number;
  abbreviation: string;
  color: string;
}

export type TSchoolSubjectListItem = Pick<
  Components.Schemas.SchoolSubjectDto,
  'id' | 'name' | 'abbreviation' | 'color' | 'groupId' | 'order'
>;

export type TSchoolSubjectGroupListItem = Pick<Components.Schemas.SchoolSubjectGroupDto, 'id' | 'name' | 'order'>;

export type TCompetenceDomainListItem = Pick<
  Components.Schemas.CompetenceDomainDto,
  'id' | 'name' | 'schoolSubjectId'
> & {
  competences: Pick<Components.Schemas.CompetenceDto, 'id' | 'description' | 'order'>[];
};

export interface IBasicTenantData {
  city: string;
  countryId?: number;
  name: string;
  schoolTypeId?: number;
  slogan?: string;
  stateId?: number;
  street: string;
  zipCode: string;
  schoolId?: number;
}

export interface IBasicReportData {
  name: string;
  calculationMethod: Components.Schemas.CalculationMethod | null;
  startDate: string | null;
  endDate: string | null;
}

export interface IReportConfiguration {
  coverSheet: Components.Schemas.ReportCoverSheet | null;
  competenceType: Components.Schemas.ReportCompetenceType | null;
  assessedCompetencesOnly: boolean;
  showGradeRecommendation: boolean;
  showCompetenceLevelDescriptions: boolean;
  showNoteCompetences: boolean;
  summarySheet: Components.Schemas.ReportSummarySheet | null;
}

/** The choices of "Förderbericht erstellen", remembered for the next report. */
export interface ISupportMeasureReportConfiguration extends ISupportMeasureReportOptions {
  /** The chips keep their selection while the signature page is switched off. */
  includeSignatures: boolean;
}

/** Which optional report sections have content; the dialog only offers those. */
export interface ISupportMeasureReportAvailableContent {
  competenceDevelopment: boolean;
  completionNote: boolean;
  materials: boolean;
}

export interface IRouteParams {
  name: keyof RouteNamedMap;
  params?: Record<string, string | number>;
}

export interface ITenantTemplateUpsertOption {
  action: TenantTemplateUpsertAction;
  label?: string;
  description?: string;
  isRecommended?: boolean;
}

export interface IBillingProductClassSelector {
  product: Components.Schemas.Product;
  label: string;
}

export interface IBumpUpClassConfig {
  generalData: Components.Schemas.CreateSchoolClassDto;
  schoolSubjects: Components.Schemas.SchoolSubjectDto[];
  team: IUserWithPermissions[];
  existingStudents: Components.Schemas.StudentDto[];
}

export interface IStatisticsFilterOption<T extends Components.Schemas.SchoolTermDto | EStatisticsCalculationType | null>
  extends ISelectOption<T> {
  amount: number | null;
}

export interface IGenericVideo {
  videoIdentifier: string;
}

export interface IVisualInfo {
  imageUrl: string | null;
  amount: number;
}
export interface ISchoolSubjectInfo extends IVisualInfo {
  schoolSubject: Components.Schemas.SchoolSubjectDto;
}

export interface IStudentOverviewEntry {
  badgeColor: string | null;
  performanceAssessment: Components.Schemas.PerformanceAssessmentDto;
  ratedAmountTotal: number;
  stats: IAssessmentVisualStat[];
}

export interface IPerformanceEntry
  extends Omit<Components.Schemas.PerformanceAssessmentEntryDto, 'performanceAssessment'> {
  performanceAssessment: Components.Schemas.BasePerformanceAssessmentDto;
}

export type TLandingContext = 'admin' | 'class';
export interface ILandingPreferenceContext {
  classId: number | null;
  context: TLandingContext;
}

export interface ILandingPreference extends ILandingPreferenceContext {
  updatedAt: string;
}
